import { ColorInput, NumberInput, SimpleGrid, Stack, TextInput } from "@mantine/core";
import { ColumnHeading, InlineField, ToggleRow } from "../../ui/fields";
import { SectionCard } from "../../ui/SectionCard";
import { PANEL_SPACE } from "../../ui/tokens";
import { sectionAllowed, withSectionAllowed, type StandardsDraft } from "../standardsModel";
import { allowLabel, type SectionProps } from "./sectionProps";

type ColorKey = "positiveColor" | "negativeColor" | "highlightColor";
type SizeKey = "headingSize" | "bodySize";

/** One hex color with its swatch (Figma color "Input"). */
function ColorField({ id, value, onChange, readOnly }: { id: string; value: string; onChange: (v: string) => void; readOnly: boolean }) {
  return (
    <ColorInput
      id={id}
      format="hex"
      placeholder="Not set"
      value={value}
      onChange={onChange}
      readOnly={readOnly}
      withPicker={!readOnly}
      withEyeDropper={false}
    />
  );
}

/**
 * Brand Configuration → Colors & Styles (Figma 14227:2527): Brand Colors (six series accents
 * beside the three semantic colors) and Brand Typography (heading and body font and size;
 * the frame's font color isn't a standard, Dan 2026-10-02).
 */
export function ColorsStylesTab({ draft, patch, canEdit, showAllowToggles }: SectionProps) {
  const readOnly = !canEdit;
  const setSeries = (i: number, v: string) => patch({ seriesColors: draft.seriesColors.map((old, j) => (j === i ? v : old)) });
  const semantic = (label: string, key: ColorKey) => (
    <InlineField key={key} label={label}>
      {(id) => <ColorField id={id} value={draft[key]} onChange={(v) => patch({ [key]: v })} readOnly={readOnly} />}
    </InlineField>
  );
  const fontColumn = (heading: string, fontKey: "headingFont" | "bodyFont", sizeKey: SizeKey, fontPlaceholder: string) => (
    <Stack gap={PANEL_SPACE.field}>
      <ColumnHeading>{heading}</ColumnHeading>
      <InlineField label="Font Family">
        {(id) => (
          <TextInput
            id={id}
            placeholder={readOnly ? "Not set" : fontPlaceholder}
            maxLength={80}
            value={draft[fontKey]}
            readOnly={readOnly}
            onChange={(e) => patch({ [fontKey]: e.currentTarget.value })}
          />
        )}
      </InlineField>
      <InlineField label="Font Size">
        {(id) => (
          <NumberInput
            id={id}
            placeholder="Not set"
            suffix="pt"
            min={6}
            max={96}
            allowDecimal={false}
            allowNegative={false}
            value={draft[sizeKey]}
            readOnly={readOnly}
            onChange={(v) => patch({ [sizeKey]: typeof v === "number" ? v : "" } as Partial<StandardsDraft>)}
          />
        )}
      </InlineField>
    </Stack>
  );

  return (
    <>
      <SectionCard title="Brand Colors">
        {showAllowToggles ? (
          <ToggleRow
            label={allowLabel("brand color")}
            checked={sectionAllowed(draft, "colors")}
            disabled={readOnly}
            onChange={(on) => patch(withSectionAllowed(draft, "colors", on))}
          />
        ) : null}
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing={PANEL_SPACE.columns} verticalSpacing={PANEL_SPACE.card}>
          <Stack gap={PANEL_SPACE.field}>
            <ColumnHeading>Series Colors</ColumnHeading>
            {draft.seriesColors.map((c, i) => (
              <InlineField key={i} label={`Accent ${i + 1}`}>
                {(id) => <ColorField id={id} value={c} onChange={(v) => setSeries(i, v)} readOnly={readOnly} />}
              </InlineField>
            ))}
          </Stack>
          <Stack gap={PANEL_SPACE.field}>
            <ColumnHeading>Semantic Colors</ColumnHeading>
            {semantic("Positive", "positiveColor")}
            {semantic("Negative", "negativeColor")}
            {semantic("Highlight", "highlightColor")}
          </Stack>
        </SimpleGrid>
      </SectionCard>

      <SectionCard title="Brand Typography">
        {showAllowToggles ? (
          <ToggleRow
            label={allowLabel("brand typography")}
            checked={sectionAllowed(draft, "typography")}
            disabled={readOnly}
            onChange={(on) => patch(withSectionAllowed(draft, "typography", on))}
          />
        ) : null}
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing={PANEL_SPACE.columns} verticalSpacing={PANEL_SPACE.card}>
          {fontColumn("Slide Headers", "headingFont", "headingSize", "Georgia")}
          {fontColumn("Body & Chart Text", "bodyFont", "bodySize", "Arial")}
        </SimpleGrid>
      </SectionCard>
    </>
  );
}
